import { useEffect, useRef } from "react";
import { extractTitleFromPRD } from "../utils/prdUtils";

/**
 * Custom hook for loading session history
 * @param {string} backendUrl - The backend API URL
 * @param {string|null} currentSessionId - The current session ID
 * @param {Function} setMessages - Function to update messages
 * @param {Function} setCurrentPrdContent - Function to update PRD content
 * @param {Function} updateSessionTitle - Function to update session title
 */
export const useSessionHistory = (
  backendUrl,
  currentSessionId,
  setMessages,
  setCurrentPrdContent,
  updateSessionTitle
) => {
  // Track which sessions have already loaded history to avoid re-loading
  const loadedSessions = useRef(new Set());

  useEffect(() => {
    const loadHistory = async () => {
      if (!currentSessionId) return;

      // Capture session ID to avoid closure issues
      const sessionId = currentSessionId;

      // Skip if already loaded for this session
      if (loadedSessions.current.has(sessionId)) {
        return;
      }

      try {
        // Get user_id from localStorage
        const userId = localStorage.getItem("userId");
        const url = userId
          ? `${backendUrl}/sessions/${sessionId}/history?user_id=${userId}`
          : `${backendUrl}/sessions/${sessionId}/history`;
        
        const res = await fetch(url);

        if (!res.ok) {
          console.error(`[History] Failed to load history: ${res.status}`);
          if (res.status === 404) {
            setMessages([
              {
                id: 1,
                username: "System",
                text: "Welcome! Start chatting to build your EWA.",
                isSystem: true,
              },
            ]);
          } else if (res.status === 403) {
            setMessages([
              {
                id: 1,
                username: "System",
                text: "Access denied: You don't have permission to view this session.",
                isSystem: true,
                isError: true,
              },
            ]);
          }
          return;
        }

        const data = await res.json();

        if (data.prd) {
          setCurrentPrdContent(data.prd, sessionId);
          const title = extractTitleFromPRD(data.prd);
          if (title) {
            updateSessionTitle(sessionId, title);
          }
        }

        // Convert backend chat arrays to UI messages
        const historyMessages = [];
        const pushMsgs = (arr) => {
          if (!Array.isArray(arr)) return;
          arr.forEach((m, idx) => {
            const text = m.content || m.text || "";
            const role = m.role || "system";
            historyMessages.push({
              id: Date.now() + idx + Math.random(),
              username:
                role === "user"
                  ? "User"
                  : role === "assistant"
                  ? "Assistant"
                  : "System",
              text,
              isUser: role === "user",
              isAssistant: role === "assistant",
              isSystem: role !== "user" && role !== "assistant",
            });
          });
        };

        pushMsgs(data.prd_chat);
        pushMsgs(data.ewa_chat);

        if (historyMessages.length > 0) {
          setMessages([
            {
              id: 1,
              username: "System",
              text: `Loaded session ${sessionId} with ${historyMessages.length} message(s).`,
              isSystem: true,
            },
            ...historyMessages,
          ]);
        } else {
          setMessages([
            {
              id: 1,
              username: "System",
              text: `Session ${sessionId} loaded. Start chatting to build your EWA.`,
              isSystem: true,
            },
          ]);
        }

        // Mark this session as loaded
        loadedSessions.current.add(sessionId);
      } catch (e) {
        console.error("[History] Failed to load session history:", e);
        setMessages([
          {
            id: 1,
            username: "System",
            text: `Failed to load history for session ${sessionId}. You can still continue chatting.`,
            isSystem: true,
            isError: true,
          },
        ]);
        // Mark as loaded even on error to avoid retry loops
        loadedSessions.current.add(sessionId);
      }
    };
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSessionId,
    backendUrl,
    updateSessionTitle,
    // Note: setMessages and setCurrentPrdContent are stable useState setters,
    // so they don't need to be in the dependency array
  ]);
};
